import { defineComponent as _defineComponent } from 'vue'
import * as validation from 'JS/components/blain-form/validation';
import BForm from 'JS/components/blain-form/BForm.vue';
import bugsnagClient from 'JS/global/bugsnag';
import ErrorBanner from 'JS/components/ErrorBanner.vue';
import ExternalAuthLogin from './ExternalAuthLogin.vue';
import HorizontalTextDivider from 'JS/components/HorizontalTextDivider.vue';
import LinkButton from 'JS/components/LinkButton.vue';
import { BCheckbox, BSubmitButton, BTextField } from "Src/js/components/blain-form";
import { nextTick, onMounted, ref, watch } from 'vue';
import { v4 as guuid } from 'uuid';
import { IAuthenticateResource, IAuthenticateResponse, LoginMethod, LoginPageType } from 'JS/types/Account';
import { useServices } from '../../composables/services';


export default /*#__PURE__*/_defineComponent({
  __name: 'SignInPage',
  props: {
    isCaptchaRequired: { type: Boolean },
    skipCartMerge: { type: Boolean },
    antiForgeryToken: null
  },
  emits: ["change-page", "loading", "require-captcha", "go-to-rewards", "signed-in", "email-changed"],
  setup(__props: any, { emit }: { emit: ({
	(event: 'change-page', page: LoginPageType): void;
	(event: 'loading', value: boolean): void;
	(event: 'require-captcha'): void;
	(event: 'go-to-rewards'): void;
	(event: 'signed-in', isRewardsMember: boolean): void;
	(event: 'email-changed', email: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	isCaptchaRequired: boolean,
	skipCartMerge: boolean,
	antiForgeryToken: string,
}



// emitting require-captcha allows us to remember that captcha is required
// even after closing and reopening the sidemenu.


const { accountService } = useServices();

const uuid = guuid();
const errorMessage = ref<string>("");
const emailAddress = ref<string>("");
const password = ref<string>("");
const isRememberMeChecked = ref<boolean>(true);
const showCaptcha = ref<boolean>(false);
const captcha = ref<string>("");
const captchaInterval = ref(null);
const headerFocuser = ref<HTMLElement | null>(null);

async function handleSignIn() {
	try {
		emit('loading', true);
		const attemptLoginData: IAuthenticateResource = {
			userName: emailAddress.value,
			password: password.value,
			saveLogin: isRememberMeChecked.value,
			hasCaptcha: showCaptcha.value,
			captcha: captcha.value,
			skipCartMerge: props.skipCartMerge ?? false,
		};
		const results: IAuthenticateResponse = await accountService.attemptLogin(attemptLoginData, props.antiForgeryToken);
		if (results.success) {
			window.blainGtmDataLayer.push({
				'loginMethod': LoginMethod.Local,
				'event': "login"
			});
			if (results.passwordChangeRequired && results.redirectUrl) {
				location.href = results.redirectUrl;
			} else {
				emit('signed-in', results.isRewardsMember);
			}
		} else {
			if (results.captchaFailed) {
				errorMessage.value = "<span class='bold'>reCAPTCHA validation failed.</span> Please try again.";
				window.grecaptcha.reset();
			} else if (results.captchaRequired && !showCaptcha.value) {
				errorMessage.value = "<span class='bold'>Let’s double check this.</span> Please re-enter your password and complete the CAPTCHA below.";
				captchaInterval.value = setInterval(checkForCaptcha, 500);
			} else {
				errorMessage.value = "<span class='bold'>Invalid Login</span> — Please try again.";
				if (results.captchaRequired) {
					window.grecaptcha.reset();
				}
			}
			password.value = "";
			emit('loading', false);
		}
	} catch (err) {
		errorMessage.value = "<span class='bold'>Something went wrong.</span> Please try again later.";
		emit('loading', false);
		bugsnagClient.notify(err);
	}
}

function setupCaptcha() {
	const recaptchaScript = document.createElement('script');
	recaptchaScript.setAttribute('src', '//www.google.com/recaptcha/api.js?render=explicit');
	document.head.appendChild(recaptchaScript);
	if (props.isCaptchaRequired) {
		captchaInterval.value = setInterval(checkForCaptcha, 500);
	}
}

function checkForCaptcha() {
	if (window.grecaptcha?.render) {
		clearInterval(captchaInterval.value);
		loadCaptcha();
	}
}

function loadCaptcha() {
	if (showCaptcha.value) {
		window.grecaptcha.reset();
	} else {
		showCaptcha.value = true;
		emit('require-captcha');
		window.grecaptcha.render("loginCaptcha" + uuid, {
			'sitekey': "6LeqjvsSAAAAAMxycMwxI2rtpYLUbzBfHHEjDvki",
			'callback': (response: string) => {
				captcha.value = response;
			}
		});
	}
}

onMounted(()=> {
	setupCaptcha();
	nextTick(()=> headerFocuser.value?.focus());
});

watch(emailAddress, (newEmail) => {
	emit('email-changed', newEmail);
});


return { __sfc: true,props, emit, accountService, uuid, errorMessage, emailAddress, password, isRememberMeChecked, showCaptcha, captcha, captchaInterval, headerFocuser, handleSignIn, setupCaptcha, checkForCaptcha, loadCaptcha, validation, BForm, ErrorBanner, ExternalAuthLogin, HorizontalTextDivider, LinkButton, BCheckbox, BSubmitButton, BTextField }
}

})